import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectBufferRadiusMetres,
    setBufferRadiusMetres
} from 'client/store/drawSlice/drawSlice';
import { useLineDrawDimensions } from 'client/utils/auth/userPermissions';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import { log } from 'client/utils/logging/logging';
import styles from './LineWidthField.module.scss';

const MIN_WIDTH_METRES = 1;
const MAX_WIDTH_METRES = 50;

const LineWidthField = ({ ...props }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const bufferRadiusMetres = useSelector(selectBufferRadiusMetres);
    const rawBufferRadiusMetres = useSelector(state => state?.draw?.bufferRadiusMetres);
    const dimensions = useLineDrawDimensions();
    const maxWidthMetres = Math.min(dimensions.buffer * 2, MAX_WIDTH_METRES);

    const [inputValue, setInputValue] = useState(String(bufferRadiusMetres * 2));
    const inputRef = useRef(null);

    // Set the default buffer radius from the user's AOI_DRAW_LINE permission only
    // on initial LINE mode entry (raw value is null). Skip on remount (e.g. edit mode)
    // so the user's chosen buffer is preserved.
    useEffect(() => {
        if (rawBufferRadiusMetres === null && Number.isFinite(dimensions.buffer) && dimensions.buffer > 0) {
            dispatch(setBufferRadiusMetres(dimensions.buffer));
        }
    }, [rawBufferRadiusMetres, dimensions.buffer, dispatch]);

    useEffect(() => {
        setInputValue(String(bufferRadiusMetres * 2));
    }, [bufferRadiusMetres]);

    useEffect(() => {
        log(
            'LineWidthField',
            `Line draw limitations sourced from ProfileService (AOI_DRAW_LINE). Max length: ${dimensions.length}m, max buffer: ${dimensions.buffer}m`
        );
    }, [dimensions]);

    const commitValue = useCallback((raw) => {
        const numericValue = parseInt(raw, 10);
        if (Number.isFinite(numericValue) && numericValue >= MIN_WIDTH_METRES) {
            const clampedValue = Math.min(numericValue, maxWidthMetres);
            dispatch(setBufferRadiusMetres(clampedValue / 2));
        }
    }, [dispatch, maxWidthMetres]);

    const handleChange = useCallback((e) => {
        const raw = e.target.value;
        if (raw === '') {
            setInputValue('');
            return;
        }

        const numericValue = parseInt(raw, 10);
        if (!Number.isFinite(numericValue) || numericValue < 0) {
            return;
        }

        const clampedValue = Math.min(numericValue, maxWidthMetres);
        setInputValue(String(clampedValue));

        if (clampedValue >= MIN_WIDTH_METRES) {
            dispatch(setBufferRadiusMetres(clampedValue / 2));
        }
    }, [dispatch, maxWidthMetres]);

    const handleKeyDown = useCallback((e) => {
        if (['e', 'E', '.', '+', '-'].includes(e.key)) {
            e.preventDefault();
            return;
        }

        if (e.key === 'Enter') {
            commitValue(inputValue);
            return;
        }

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const current = Number(inputValue) || 0;
            let next;
            if (e.key === 'ArrowUp') {
                next = Math.floor(current) + 1;
            } else {
                next = Math.ceil(current) - 1;
                if (next < MIN_WIDTH_METRES && current > MIN_WIDTH_METRES) {
                    next = MIN_WIDTH_METRES;
                }
            }

            if (next < MIN_WIDTH_METRES || next > maxWidthMetres) {
                return;
            }

            setInputValue(String(next));
            dispatch(setBufferRadiusMetres(next / 2));
        }
    }, [commitValue, inputValue, dispatch, maxWidthMetres]);

    const handleBlur = useCallback(() => {
        commitValue(inputValue);
    }, [commitValue, inputValue]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const current = Number(inputValue) || 0;
        let next;
        if (e.deltaY < 0) {
            next = Math.floor(current) + 1;
        } else {
            next = Math.ceil(current) - 1;
            if (next < MIN_WIDTH_METRES && current > MIN_WIDTH_METRES) {
                next = MIN_WIDTH_METRES;
            }
        }

        if (next < MIN_WIDTH_METRES || next > maxWidthMetres) {
            return;
        }

        setInputValue(String(next));
        dispatch(setBufferRadiusMetres(next / 2));
    }, [inputValue, dispatch, maxWidthMetres]);

    // Attach wheel listener with { passive: false } so preventDefault works.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) {
            return;
        }
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    const label = intl.formatMessage({ id: 'lineWidthField.label' });
    const maxHint = intl.formatMessage({ id: 'lineWidthField.max' }, { maxBuffer: maxWidthMetres });
    const unit = intl.formatMessage({ id: 'lineWidthField.unit' });

    return (
        <div
            className={styles.lineWidthField}
            data-testid={props['data-testid'] || 'lineWidthField'}
        >
            <div className={styles.lineWidthRow}>
                <label className={styles.label} htmlFor="lineWidthInput">
                    {label}
                </label>
                <span className={styles.maxHint} data-testid="lineWidthField.maxHint">
                    {maxHint}
                </span>
            </div>
            <div className={styles.inputRow}>
                <input
                    id="lineWidthInput"
                    ref={inputRef}
                    className={[
                        'govuk-input',
                        styles.input
                    ].filter(Boolean).join(' ')}
                    type="number"
                    min={MIN_WIDTH_METRES}
                    max={maxWidthMetres}
                    step="1"
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    data-testid="lineWidthField.input"
                />
                <span className={styles.unit}>{unit}</span>
            </div>

        </div>
    );
};

LineWidthField.propTypes = {
    ...defaultAriaProps
};

export default LineWidthField;
